import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ElementRef,
  effect,
  inject
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { StatusResponse } from '../../models/statusResponse';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { MenuItem } from 'primeng/api';
import { BatchResponse } from '../../models/sdk-interfaces';
import { distinctUntilChanged, Observable, of, switchMap } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { Bind } from 'primeng/bind';
import { MenuModule } from 'primeng/menu';
import { Button } from 'primeng/button';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { NgClass } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { DocumentExpirationWarningComponent } from '../document-expiration-warning/document-expiration-warning.component';
import { isExpiringSoon } from '../document-expiration-warning/document-expiration.util';

export interface FileSelectorAttachment {
  name: string;
  id: string;
  completed: boolean;
  status: StatusResponse;
  classificationStarted: boolean;
  canceled?: boolean;
  fileContent?: string;
  selected?: boolean;
  expirationDate?: string;
  isExpired?: boolean;
  documentTypeCode?: string;
}

@Component({
  selector: 'app-file-selector',
  templateUrl: './file-selector.component.html',
  styleUrls: ['./file-selector.component.scss'],
  imports: [
    Bind,
    MenuModule,
    Button,
    NgxSpinnerComponent,
    NgClass,
    Tooltip,
    TranslatePipe,
    Dialog,
    DocumentExpirationWarningComponent
  ]
})
export class FileSelectorComponent implements OnInit, OnChanges {
  userPrivilegesService = inject(UserPrivilegesService);
  private translateService = inject(TranslateService);
  private featureFlagService = inject(FeatureFlagService);

  private canScan = toSignal(
    this.featureFlagService.getFlag('feature-web-capture-scanning', false).pipe(
      switchMap((scanningEnabled: boolean) => {
        if (!scanningEnabled) {
          return of(false);
        }
        return this.canPerformAction('ETRIEVE_CONTENT_SCAN');
      }),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );

  docExpirationUiEnabled = toSignal(
    this.featureFlagService.getFlag('feature-capture-doc-expiration-ui', false)
  );

  @Input() attachments: FileSelectorAttachment[] = [];
  @Input() selectedDocumentId: string | null = null;
  /**
   * @deprecated Use selectedDocumentId instead.
   * Kept for any templates not yet migrated to fileId-based selection.
   */
  @Input() currentAttachmentIndex: number = 0;
  @Input() batch: BatchResponse = {} as BatchResponse;
  @Input() isBatchCompleted: boolean = false;
  @Input() enableBatchOptions: boolean = false;
  @Input() showDocumentOptions: boolean = false;
  @Input() isLoadingFiles: boolean = false;
  @Input() batchLoading: boolean = false;
  @Input() isAutoClassificationEnabled: boolean = false;
  @Input() canCloseBatch: boolean = false;
  @Input() disabled: boolean = false;

  @Output() selectDocumentEvent = new EventEmitter<string>();
  @Output() openEditBatchEvent = new EventEmitter();
  @Output() deleteBatchEvent = new EventEmitter();
  @Output() closeBatchEvent = new EventEmitter();
  @Output() closeDocumentsEvent = new EventEmitter();
  @Output() openAddDocumentsEvent = new EventEmitter();
  @Output() openScanningDialogEvent = new EventEmitter();
  @Output() openMergeDocumentsEvent = new EventEmitter();
  @Output() openUpdateDocumentsEvent = new EventEmitter();

  classificationStarted: boolean = false;
  documentListHeader = 'documents';
  batchOptions: MenuItem[] = [];
  documentOptions: MenuItem[] = [];
  showCloseDocumentsDialog: boolean = false;
  private showScanningMenuOption: boolean = false;

  @ViewChild('labelRef', { static: false }) labelRef!: ElementRef;

  constructor() {
    effect(() => {
      this.showScanningMenuOption = this.canScan();
      this.refreshMenuOptions();
    });
  }

  /**
   * Checks if text content is overflowing in the given element
   * @param element - HTML element to check for text overflow
   */
  isTextOverflowing(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return element.offsetWidth < element.scrollWidth;
  }

  /**
   * Gets the appropriate menu options based on if user is in a batch or in adhoc upload
   */
  get menuOptions(): MenuItem[] {
    if (this.enableBatchOptions) {
      return this.batchOptions;
    }
    return this.documentOptions;
  }

  /**
   * Angular lifecycle hook - initializes component and sets up menu options
   */
  ngOnInit() {
    this.refreshMenuOptions();
  }

  /**
   * Keeps batch menu item states in sync with input changes.
   * @param changes Changed input values
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isBatchCompleted']) {
      this.refreshMenuOptions();
    }
  }

  /**
   * Rebuilds all menu options from current component state.
   */
  private refreshMenuOptions(): void {
    this.documentOptions = [
      {
        label: this.translateService.instant('mergeDocuments'),
        icon: 'fa-solid fa-merge',
        visible: true,
        command: () => this.openMergeDocuments()
      }
    ];

    const batchOptions: MenuItem[] = [
      {
        label: this.translateService.instant('documentListMenuOptions.batchActions'),
        items: [
          {
            label: this.translateService.instant('documentListMenuOptions.editBatch'),
            icon: 'fa-solid fa-pen-to-square',
            disabled: this.isBatchCompleted,
            command: () => this.openEditBatch()
          },
          {
            label: this.translateService.instant('documentListMenuOptions.addDocuments'),
            icon: 'fa-solid fa-plus',
            visible: true,
            command: () => this.openAddDocuments()
          },
          {
            label: this.translateService.instant('documentListMenuOptions.addScannedDocument'),
            icon: 'fa-solid fa-scanner-image',
            visible: this.showScanningMenuOption,
            command: () => this.openScanningDialog()
          },
          {
            label: this.translateService.instant('documentListMenuOptions.deleteBatch'),
            icon: 'fa-solid fa-trash delete-batch-icon',
            command: () => this.deleteBatch()
          }
        ]
      },
      { separator: true },
      {
        label: this.translateService.instant('documentListMenuOptions.documentActions'),
        items: [
          {
            label: this.translateService.instant('mergeDocuments'),
            icon: 'fa-solid fa-merge',
            disabled: this.isBatchCompleted,
            command: () => this.openMergeDocuments()
          },
          {
            label: this.translateService.instant('documentListMenuOptions.updateDocuments'),
            icon: 'fa-solid fa-arrow-rotate-right',
            disabled: this.isBatchCompleted,
            command: () => this.openUpdateDocuments()
          }
          // TODO: In place for next phase
          // {
          //   label: this.translateService.instant('documentListMenuOptions.deleteDocuments'),
          //   icon: 'fa-solid fa-trash delete-batch-icon'
          //   // command: () => this.deleteBatch()
          // }
        ]
      }
    ];

    this.batchOptions = batchOptions;
  }

  /**
   * Gets the appropriate header label based on classification status
   */
  get headerLabel(): string {
    return this.classificationStarted ? 'fileSelectorHeader1' : 'fileSelectorHeader2';
  }

  /**
   * Selects a document by fileId.
   * @param index  - The display index (kept for legacy consumers only)
   */
  selectAttachment(index: number): void {
    const attachment = this.attachments[index];
    if (attachment) {
      this.selectDocumentEvent.emit(attachment.id);
    }
  }

  /**
   * Opens the edit batch dialog
   */
  openEditBatch(): void {
    this.openEditBatchEvent.emit();
  }

  /**
   * Opens the add documents dialog
   */
  openAddDocuments(): void {
    this.openAddDocumentsEvent.emit();
  }

  /**
   * Opens the scanning dialog
   */
  openScanningDialog(): void {
    this.openScanningDialogEvent.emit();
  }

  /**
   * Opens merge documents
   */
  openMergeDocuments(): void {
    this.openMergeDocumentsEvent.emit();
  }

  /**
   * Opens update documents
   */
  openUpdateDocuments(): void {
    this.openUpdateDocumentsEvent.emit();
  }

  /**
   * Deletes the current batch
   */
  deleteBatch(): void {
    this.deleteBatchEvent.emit();
  }

  /**
   * Closes the current batch
   */
  closeBatch(): void {
    this.closeBatchEvent.emit();
  }

  /**
   * Closes the current documents upload session and runs restartAdhocUpload to start a new session.
   */
  closeDocuments(): void {
    this.closeDocumentsEvent.emit();
    this.showCloseDocumentsDialog = false;
  }

  /**
   * Handles the cancellation of the close documents action, hiding the confirmation dialog
   */
  handleCancelCloseDocuments(): void {
    this.showCloseDocumentsDialog = false;
  }
  /**
   * Checks if the user can perform a specific action
   * @param actionCode - Code representing the action to check
   */
  canPerformAction(actionCode: string): Observable<boolean> {
    return this.userPrivilegesService.canPerformAction(actionCode);
  }

  /**
   * Whether the document's expiration date is within a week of expiring,
   * used to apply the file warning background.
   * @param expirationDate - ISO date string the document expires on, or null/undefined if it has none.
   */
  isExpiringSoon(expirationDate: string | null | undefined): boolean {
    return this.docExpirationUiEnabled() && isExpiringSoon(expirationDate);
  }
}
